/**
 * @file src/local/localAudioIds.js
 * @description The identity of a user's own file: `local:<uuid>`.
 *
 * It lives in the `local` namespace the offline key scheme reserved (`offlineKeys.js`), and it is
 * used everywhere a track id would be — the drawer's voiceId, the engine's trackId, the layout, the
 * telemetry — so every id-keyed path has to tolerate it. It is never a faked track id: nothing
 * about a file on disk has a platform id or a release version, and a fabricated one could not be
 * taken back once something was filed under it.
 */
import { KEY_NAMESPACE } from '../offline/offlineKeys.js';

const PREFIX = `${KEY_NAMESPACE.LOCAL}:`;

function randomUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Mint a new local entry id. */
export function createLocalEntryId() {
  return `${PREFIX}${randomUuid()}`;
}

/** True for any id in the local namespace. Safe on null and non-strings. */
export function isLocalEntryId(id) {
  return typeof id === 'string' && id.startsWith(PREFIX);
}

/** The stand-in a local id becomes in anything that leaves the machine. */
export const LOCAL_ID_REDACTED = 'local';
